import { Context } from '../context/Context';
import { Camera } from '../camera';
import { RenderTexture } from '../renderer/RenderTarget';
import { OverlayShader } from '../shader/generated';
import { OverlayManager } from './OverlayManager';
import type { OverlayQuad } from './Overlay';
import type { MsdfFont } from './MsdfFont';

// Per-instance layout:
//   3 floats: iWorldPos
//   2 floats: iSizePx
//   2 floats: iScreenOffsetPx
//   4 floats: iColor
//   2 floats: iUvOrigin
//   2 floats: iUvSize
const FLOATS_PER_INSTANCE = 15;
const STRIDE_BYTES = FLOATS_PER_INSTANCE * Float32Array.BYTES_PER_ELEMENT;

// Two triangles → 6 unindexed verts. Corner range matches the vertex shader's
// `aQuadCorner` expectation of [-0.5, 0.5].
const QUAD_CORNERS = new Float32Array([
  -0.5, -0.5,
   0.5, -0.5,
  -0.5,  0.5,
  -0.5,  0.5,
   0.5, -0.5,
   0.5,  0.5,
]);

interface Batch {
  font: MsdfFont | null;
  depthTested: boolean;
  quads: OverlayQuad[];
}

/**
 * Draws every quad emitted by {@link OverlayManager}'s overlays in one
 * instanced draw per (font, depthTested) batch. Runs after FXAA so the
 * temporal/edge passes don't smear or hunt for edges over overlay pixels.
 *
 * Procedural quads (no font) and MSDF text quads share one shader; the
 * `uHasMsdf` uniform selects the fragment-shader branch per batch.
 */
export class OverlayPass {
  private vao: WebGLVertexArrayObject | null = null;
  private quadBuffer: WebGLBuffer | null = null;
  private instanceBuffer: WebGLBuffer | null = null;
  private instanceCapacity = 0;
  private cpuInstances: Float32Array = new Float32Array(0);
  private readonly scratchQuads: OverlayQuad[] = [];
  /**
   * Reused per-(font, depthTested) batches. Grown lazily to the max distinct
   * combos seen this session; `quads.length = 0` resets without freeing.
   * Linear scan to find the right bucket — `N` (unique combos) is typically
   * 2–4 (procedural ± depth, plus one font per writing surface), so a Map +
   * composite key would cost more in allocation than it saves in lookup.
   */
  private readonly batches: Batch[] = [];
  private lastTimeMs = 0;

  public init(ctx: Context): void {
    const gl = ctx.gl;

    this.quadBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, QUAD_CORNERS, gl.STATIC_DRAW);

    this.instanceBuffer = gl.createBuffer()!;
    this.ensureInstanceCapacity(gl, 256);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    // location 1: iWorldPos vec3
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, STRIDE_BYTES, 0);
    gl.vertexAttribDivisor(1, 1);
    // location 2: iSizePx vec2
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, STRIDE_BYTES, 3 * 4);
    gl.vertexAttribDivisor(2, 1);
    // location 3: iScreenOffsetPx vec2
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 2, gl.FLOAT, false, STRIDE_BYTES, 5 * 4);
    gl.vertexAttribDivisor(3, 1);
    // location 4: iColor vec4
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.FLOAT, false, STRIDE_BYTES, 7 * 4);
    gl.vertexAttribDivisor(4, 1);
    // location 5: iUvOrigin vec2
    gl.enableVertexAttribArray(5);
    gl.vertexAttribPointer(5, 2, gl.FLOAT, false, STRIDE_BYTES, 11 * 4);
    gl.vertexAttribDivisor(5, 1);
    // location 6: iUvSize vec2
    gl.enableVertexAttribArray(6);
    gl.vertexAttribPointer(6, 2, gl.FLOAT, false, STRIDE_BYTES, 13 * 4);
    gl.vertexAttribDivisor(6, 1);

    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  public render(
    ctx: Context,
    camera: Camera,
    manager: OverlayManager,
    sceneDepth: RenderTexture,
    output: WebGLFramebuffer | null,
  ): void {
    if (manager.size === 0) return;

    const now = performance.now();
    // Clamp dt to avoid huge jumps if the tab was backgrounded or a frame
    // stalled — short-lived animated overlays (damage numbers) would
    // otherwise expire in a single tick on the resumed frame.
    const rawDt = this.lastTimeMs > 0 ? (now - this.lastTimeMs) / 1000 : 0;
    const dt = Math.min(rawDt, 0.1);
    this.lastTimeMs = now;

    const quads = this.scratchQuads;
    quads.length = 0;
    manager.collect(quads, dt, camera.cameraPosition);
    if (quads.length === 0) return;

    // Group by (font, depthTested) into the reused batch pool. New combos
    // grow the pool; existing combos are reset and refilled. No per-frame
    // allocation once the pool has seen every combo at least once.
    for (let i = 0; i < this.batches.length; i++) this.batches[i].quads.length = 0;
    for (const q of quads) {
      const font = q.font ?? null;
      const depthTested = q.depthTested;
      let batch: Batch | null = null;
      for (let i = 0; i < this.batches.length; i++) {
        const b = this.batches[i];
        if (b.font === font && b.depthTested === depthTested) {
          batch = b;
          break;
        }
      }
      if (!batch) {
        batch = { font, depthTested, quads: [] };
        this.batches.push(batch);
      }
      batch.quads.push(q);
    }

    const gl = ctx.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, output);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const shader = ctx.engine.shaderManager.getOverlayShader();
    ctx.setShader(shader);

    shader.uniformsContainer
      .setViewProjection(camera.unjitteredViewProjection)
      .setInvDisplaySize([1.0 / ctx.viewPort.displayWidth, 1.0 / ctx.viewPort.displayHeight])
      .setSceneDepth(sceneDepth);

    gl.bindVertexArray(this.vao);

    // Procedural (font = null) before text so text floats over panels. Two
    // sequential passes over the pool — no intermediate sorted array.
    for (let i = 0; i < this.batches.length; i++) {
      const b = this.batches[i];
      if (b.quads.length > 0 && b.font === null) this.drawBatch(gl, shader, b);
    }
    for (let i = 0; i < this.batches.length; i++) {
      const b = this.batches[i];
      if (b.quads.length > 0 && b.font !== null) this.drawBatch(gl, shader, b);
    }

    gl.bindVertexArray(null);

    // Restore expected post-pass state for the legacy Overlay-mode block that
    // runs after us in DeferredSceneRenderer.
    gl.disable(gl.BLEND);
    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
  }

  private drawBatch(gl: WebGL2RenderingContext, shader: OverlayShader, batch: Batch): void {
    shader.uniformsContainer.setDepthTested(batch.depthTested);
    if (batch.font) {
      shader.uniformsContainer
        .setHasMsdf(true)
        .setMsdf(batch.font.texture)
        .setPxRange(batch.font.distanceRange)
        .setAtlasSize([batch.font.atlasWidth, batch.font.atlasHeight]);
    } else {
      shader.uniformsContainer.setHasMsdf(false);
    }
    shader.validate();
    this.uploadAndDraw(gl, batch.quads);
  }

  public destroy(gl: WebGL2RenderingContext): void {
    if (this.vao) gl.deleteVertexArray(this.vao);
    if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
    if (this.instanceBuffer) gl.deleteBuffer(this.instanceBuffer);
    this.vao = null;
    this.quadBuffer = null;
    this.instanceBuffer = null;
    this.instanceCapacity = 0;
    this.cpuInstances = new Float32Array(0);
  }

  private uploadAndDraw(gl: WebGL2RenderingContext, quads: OverlayQuad[]): void {
    if (quads.length > this.instanceCapacity) {
      this.ensureInstanceCapacity(gl, Math.max(quads.length, this.instanceCapacity * 2));
    }

    const cpu = this.cpuInstances;
    for (let i = 0; i < quads.length; i++) {
      const base = i * FLOATS_PER_INSTANCE;
      const q = quads[i];
      cpu[base + 0] = q.worldPos[0];
      cpu[base + 1] = q.worldPos[1];
      cpu[base + 2] = q.worldPos[2];
      cpu[base + 3] = q.sizePx[0];
      cpu[base + 4] = q.sizePx[1];
      cpu[base + 5] = q.screenOffsetPx[0];
      cpu[base + 6] = q.screenOffsetPx[1];
      const a = q.alphaScale === undefined ? q.color[3] : q.color[3] * q.alphaScale;
      cpu[base + 7] = q.color[0];
      cpu[base + 8] = q.color[1];
      cpu[base + 9] = q.color[2];
      cpu[base + 10] = a;
      cpu[base + 11] = q.uvOrigin ? q.uvOrigin[0] : 0;
      cpu[base + 12] = q.uvOrigin ? q.uvOrigin[1] : 0;
      cpu[base + 13] = q.uvSize ? q.uvSize[0] : 0;
      cpu[base + 14] = q.uvSize ? q.uvSize[1] : 0;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, cpu, 0, quads.length * FLOATS_PER_INSTANCE);

    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, quads.length);
  }

  private ensureInstanceCapacity(gl: WebGL2RenderingContext, capacity: number): void {
    this.instanceCapacity = capacity;
    this.cpuInstances = new Float32Array(capacity * FLOATS_PER_INSTANCE);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    // bufferData reallocates storage on the same buffer object; VAO references
    // captured by vertexAttribPointer remain valid.
    gl.bufferData(gl.ARRAY_BUFFER, this.cpuInstances.byteLength, gl.DYNAMIC_DRAW);
  }
}
